import * as THREE from 'three';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import {
  addSceneText,
  setPushIntersectedObject,
  update_assets_data,
  selected_object,
} from '../../redux/Slice/gridSlice';
import store from '../../redux/store';
import { TextInitialData } from '../../utils/constant';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import { isLiveSceneObject3D } from '../../utils/ThreeJsUtils/assetVisibilityUtils';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import toast from 'react-hot-toast';
import { clone } from 'lodash';
import { getNextTextName } from './TextCounter';
import {
  attachEventsToSceneObject,
  normalizeEventsForScene,
  buildPublishedAssetIdLookup,
} from '../../utils/eventAssetUtils';
import {
  isGridHistoryRestoreInProgress,
  isGridHistorySuppressed,
} from '../../utils/gridHistorySuppress';
import { buildTextReduxAssetRecord } from '../../utils/textAssetUtils';
import { applyStoredPivotContentMatrix, applyStoredPivotOffset } from '../../utils/ThreeJsUtils/pivotEditor';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { copyPhysicsToDuplicate } from '../../utils/Physics/PhysicsUtils';
import { buildTempFileName } from '../../utils/NamingUtils';

const TEXT_2D_EDITOR_SCALE_Z = 0.000001;

const TEXT_GEOMETRY_SIGNATURE_KEYS = [
  'content',
  'fontSize',
  'fontFamily',
  'fontWeight',
  'textType',
  'extrusion',
  'bevelSize',
  'curveSegments',
  'horizontal',
  'vertical',
  'fontPath',
];

function getTextMaterialGeometrySignature(textMaterial) {
  if (!textMaterial) return '';
  const payload = {};
  TEXT_GEOMETRY_SIGNATURE_KEYS.forEach((key) => {
    payload[key] = textMaterial[key];
  });
  return JSON.stringify(payload);
}

function markTextMeshGeometryBuilt(textMesh) {
  const tm = textMesh?.userData?.text_material;
  if (!textMesh || !tm) return;
  textMesh.userData._builtTextGeometrySig =
    getTextMaterialGeometrySignature(tm);
}

export function textNeedsGeometryRebuild(textMesh) {
  const tm = textMesh?.userData?.text_material;
  if (!textMesh || !tm || !textMesh.geometry) return false;

  const currentSig = getTextMaterialGeometrySignature(tm);
  const builtSig = textMesh.userData?._builtTextGeometrySig;
  if (builtSig) return builtSig !== currentSig;

  // Legacy meshes: assume geometry matches until a content-affecting undo changes userData.
  markTextMeshGeometryBuilt(textMesh);
  return false;
}

function syncTextMaterialFromUserData(textMesh) {
  const tm = textMesh?.userData?.text_material;
  const material = textMesh?.material;
  if (!tm || !material) return;

  if (material.color?.set && tm.color) {
    material.color.set(tm.color);
  }
  if (material.metalness !== undefined && tm.metalness !== undefined) {
    material.metalness = tm.metalness;
  }
  if (material.roughness !== undefined && tm.roughness !== undefined) {
    material.roughness = tm.roughness;
  }
  material.needsUpdate = true;

  textMesh.content = tm.content ?? textMesh.content;
  textMesh.horizontal = tm.horizontal ?? textMesh.horizontal;
  textMesh.vertical = tm.vertical ?? textMesh.vertical;
}

export function syncTextAssetToRedux(textMesh) {
  // Skip only during undo/redo restore — not during duplicate/paste (history suppressed).
  if (isGridHistoryRestoreInProgress()) return;

  const record = buildTextReduxAssetRecord(textMesh);
  if (!record) return;

  const objectId =
    record._id ?? record.id ?? record.url ?? record.temp_file_name;
  const list = store.getState().assets_object?.assets_data?.text || [];
  const index = list.findIndex((asset) => {
    const assetId =
      asset?._id ??
      asset?.id ??
      asset?.url ??
      asset?.name ??
      asset?.temp_file_name;
    return assetId === objectId || asset?.text_material?.id === objectId;
  });

  const updated =
    index === -1
      ? [...list, record]
      : list.map((asset, i) => (i === index ? { ...asset, ...record } : asset));

  store.dispatch(
    update_assets_data({
      type: 'text',
      data: updated,
    })
  );
}

export async function rebuildTextMeshFromUserData(textMesh, object) {
  const tm = textMesh?.userData?.text_material;
  if (!textMesh || !tm) return;

  await handleAddText(
    { ...object, selectedObject: textMesh },
    { ...tm, position: null, rotation: null, scale: null },
    'add'
  );
  markTextMeshGeometryBuilt(textMesh);
}

export async function rebuildAllTextsAfterHistoryRestore(
  scene,
  objectContext = {}
) {
  if (!scene) return;

  const textMeshes = [];
  scene.traverse((obj) => {
    if (obj.deleteType === 'text' && obj.userData?.text_material) {
      textMeshes.push(obj);
    }
  });

  const rebuildTargets = [];
  for (const textMesh of textMeshes) {
    if (textNeedsGeometryRebuild(textMesh)) {
      rebuildTargets.push(textMesh);
      continue;
    }
    syncTextMaterialFromUserData(textMesh);
  }

  for (const textMesh of rebuildTargets) {
    await rebuildTextMeshFromUserData(textMesh, {
      scene,
      selectedObject: objectContext.selectedObject || textMesh,
    });
  }
}

export function getTextEditorScaleZ(params, is3D) {
  if (is3D) {
    const extrusion = Number(params?.extrusion);
    return Number.isFinite(extrusion) && extrusion > 0
      ? extrusion
      : (TextInitialData.extrusion ?? 0.001);
  }
  return TEXT_2D_EDITOR_SCALE_Z;
}

function applyTextEditorScale(textMesh, params, is3D) {
  textMesh.scale.set(1, 1, getTextEditorScaleZ(params, is3D));
}

function isPublishedUnitScale(scale) {
  if (!scale) return false;
  const x = scale.x ?? 1;
  const y = scale.y ?? 1;
  const z = scale.z ?? 1;
  return x === 1 && y === 1 && z === 1;
}

export function getTextExtrusionScaleZ(object) {
  const tm = object?.userData?.text_material || {};
  const is3D = tm.textType === '3D';
  return getTextEditorScaleZ(tm, is3D);
}

/**
 * Published text GLBs bake editor scale (incl. extrusion Z) into geometry and
 * keep the mesh at 1,1,1. Panel mapping must stay 1:1 for those meshes.
 */
export function isTextScaleBakedIntoGeometry(object) {
  return !!(object?.userData?.scaleBakedIntoGeometry);
}

function setTextScaleBakedIntoGeometry(object, baked) {
  if (!object) return;
  if (!object.userData) object.userData = {};
  if (baked) {
    object.userData.scaleBakedIntoGeometry = true;
  } else {
    delete object.userData.scaleBakedIntoGeometry;
  }
}

/** Map scene scale → transform panel values (Z extrusion shown as 1 at default). */
export function sceneScaleToPanelScale(object, scale = object?.scale) {
  if (!object || object.deleteType !== 'text' || !scale) {
    return {
      x: scale?.x ?? 1,
      y: scale?.y ?? 1,
      z: scale?.z ?? 1,
    };
  }

  // Baked GLB: panel scale === scene scale (extrusion already in vertices).
  if (isTextScaleBakedIntoGeometry(object)) {
    return {
      x: scale.x ?? 1,
      y: scale.y ?? 1,
      z: scale.z ?? 1,
    };
  }

  const baseZ = getTextExtrusionScaleZ(object);
  return {
    x: scale.x ?? 1,
    y: scale.y ?? 1,
    z: baseZ > 0 ? (scale.z ?? baseZ) / baseZ : (scale.z ?? 1),
  };
}

/** Map transform panel values → scene scale (applies extrusion on Z). */
export function panelScaleToSceneScale(object, panel) {
  if (!object || object.deleteType !== 'text') {
    return {
      x: panel.x ?? 1,
      y: panel.y ?? 1,
      z: panel.z ?? 1,
    };
  }

  if (isTextScaleBakedIntoGeometry(object)) {
    return {
      x: panel.x ?? 1,
      y: panel.y ?? 1,
      z: panel.z ?? 1,
    };
  }

  const baseZ = getTextExtrusionScaleZ(object);
  return {
    x: panel.x ?? 1,
    y: panel.y ?? 1,
    z: (panel.z ?? 1) * baseZ,
  };
}

export function applyPanelScaleToTextObject(object, x, y, z) {
  const sceneScale = panelScaleToSceneScale(object, { x, y, z });
  object.scale.set(sceneScale.x, sceneScale.y, sceneScale.z);
}

/** Apply scale from animation panel space (text default look = 1,1,1). */
export function applyAnimatedScale(obj, x, y, z) {
  if (!obj) return;
  if (obj.deleteType === 'text') {
    applyPanelScaleToTextObject(obj, x, y, z);
  } else {
    obj.scale.set(x, y, z);
  }
}

export function resolveAnimatedPanelScale(obj, effect, easedProgress) {
  const startScale = effect.startScale || { x: 1, y: 1, z: 1 };
  const endScale = effect.endScale || { x: 2, y: 2, z: 2 };
  const uniformScale =
    effect.uniformScale !== undefined ? effect.uniformScale : false;
  const scaleAxes = effect.scaleAxes ||
    effect.scaleAxis || { x: true, y: true, z: true };
  const lerp = (from, to) => from + (to - from) * easedProgress;

  const currentPanel =
    obj?.deleteType === 'text'
      ? sceneScaleToPanelScale(obj, obj.scale)
      : { x: obj?.scale?.x ?? 1, y: obj?.scale?.y ?? 1, z: obj?.scale?.z ?? 1 };

  if (uniformScale) {
    const scaleValue = lerp(startScale.x, endScale.x);
    return {
      x: scaleAxes.x ? scaleValue : currentPanel.x,
      y: scaleAxes.y ? scaleValue : currentPanel.y,
      z: scaleAxes.z ? scaleValue : currentPanel.z,
    };
  }

  return {
    x: scaleAxes.x ? lerp(startScale.x, endScale.x) : currentPanel.x,
    y: scaleAxes.y ? lerp(startScale.y, endScale.y) : currentPanel.y,
    z: scaleAxes.z ? lerp(startScale.z, endScale.z) : currentPanel.z,
  };
}

export function applyScaleEffectToObject(obj, effect, easedProgress) {
  const panelScale = resolveAnimatedPanelScale(obj, effect, easedProgress);
  applyAnimatedScale(obj, panelScale.x, panelScale.y, panelScale.z);
}

export function applyPulseScaleToObject(obj, scaleFactor, pulseAxis) {
  if (!obj) return;

  if (typeof pulseAxis === 'object') {
    if (obj.deleteType === 'text') {
      const panel = sceneScaleToPanelScale(obj, obj.scale);
      applyAnimatedScale(
        obj,
        pulseAxis.x ? scaleFactor : panel.x,
        pulseAxis.y ? scaleFactor : panel.y,
        pulseAxis.z ? scaleFactor : panel.z
      );
    } else {
      if (pulseAxis.x) obj.scale.x = scaleFactor;
      if (pulseAxis.y) obj.scale.y = scaleFactor;
      if (pulseAxis.z) obj.scale.z = scaleFactor;
    }
    return;
  }

  if (pulseAxis === 'uniform') {
    applyAnimatedScale(obj, scaleFactor, scaleFactor, scaleFactor);
    return;
  }

  if (obj.deleteType === 'text') {
    const panel = sceneScaleToPanelScale(obj, obj.scale);
    if (pulseAxis === 'x') {
      applyAnimatedScale(obj, scaleFactor, panel.y, panel.z);
    } else if (pulseAxis === 'y') {
      applyAnimatedScale(obj, panel.x, scaleFactor, panel.z);
    } else if (pulseAxis === 'z') {
      applyAnimatedScale(obj, panel.x, panel.y, scaleFactor);
    }
    return;
  }

  if (pulseAxis === 'x') obj.scale.x = scaleFactor;
  else if (pulseAxis === 'y') obj.scale.y = scaleFactor;
  else if (pulseAxis === 'z') obj.scale.z = scaleFactor;
}

/** Bake editor scene scale into geometry params for publish / reload at 1,1,1. */
export function buildPublishedTextParams(params, bakedScale, is3D) {
  const bx = bakedScale?.x ?? 1;
  const by = bakedScale?.y ?? 1;
  const bz = bakedScale?.z ?? 1;

  return {
    ...params,
    fontSize: (params?.fontSize ?? TextInitialData.fontSize) * bx,
    extrusion: is3D ? (params?.extrusion ?? TextInitialData.extrusion) * bz : 0,
    bevelSize: (params?.bevelSize ?? TextInitialData.bevelSize) * bx,
    bevelThickness: (params?.bevelThickness ?? 0.02) * bz,
  };
}

/** User-facing scale for publish / Redux (panel space, default Z = 1). */
export function getTextPublishPanelScale(object, scale = object?.scale) {
  return sceneScaleToPanelScale(object, scale);
}

function normalizeBakedScaleToPanel(bakedScale, params, is3D) {
  const baseZ = getTextEditorScaleZ(params, is3D);
  const x = bakedScale?.x ?? 1;
  const y = bakedScale?.y ?? 1;
  let z = bakedScale?.z ?? 1;

  if (baseZ > 0) {
    if (z <= baseZ * 2) {
      z = z / baseZ;
    } else if (z >= (1 / baseZ) * 0.5) {
      z = 1;
    }
  }

  return { x, y, z };
}

export function resolvePublishedBakedScale(data, params, is3D) {
  const raw =
    data?.bakedTextScale || data?.text_material?.bakedTextScale || null;
  if (raw) {
    return normalizeBakedScaleToPanel(raw, params, is3D);
  }
  return { x: 1, y: 1, z: 1 };
}

// Helper function to generate the next copy name
const getNextCopyName = (scene, originalName) => {
  const baseName = originalName.replace(/_copy\d+$/, '');

  const existingCopies = scene.children.filter((child) => {
    if (!child.file_name) return false;
    return (
      child.file_name === baseName ||
      child.file_name.startsWith(`${baseName}_copy`)
    );
  });

  const copyNumbers = existingCopies
    .map((obj) => {
      const match = obj.file_name.match(/_copy(\d+)$/);
      return match ? parseInt(match[1]) : 0;
    })
    .filter((num) => num > 0);

  const nextCopyNumber =
    copyNumbers.length > 0 ? Math.max(...copyNumbers) + 1 : 1;

  return `${baseName}_copy${nextCopyNumber}`;
};

// Helper function to create multi-line text with proper alignment
const createAlignedTextGeometry = (text, font, params, horizontal) => {
  const lines = text.split('\n');
  const is3D = params.textType == '3D';

  // ExtrudeGeometry bevel must fit within roughly half the extrusion depth,
  // or the contour math goes degenerate and the mesh balloons/self-intersects.
  const depth = is3D ? Number(params.extrusion) || 0 : 0;
  const maxBevel = is3D
    ? depth / 2
    : (params.bevelThickness ?? params.bevelSize ?? 0.02);
  const safeBevelThickness = Math.min(
    params.bevelThickness ?? params.bevelSize ?? 0.02,
    maxBevel
  );
  const safeBevelSize = Math.min(params.bevelSize ?? 0.02, maxBevel);

  // Create geometry for each line to measure width
  const lineGeometries = lines.map((line) => {
    return new TextGeometry(line || ' ', {
      font: font,
      size: params.fontSize * 0.01,
      height: depth,
      curveSegments: params.curveSegments,
      bevelEnabled: is3D ? true : false,
      bevelThickness: safeBevelThickness,
      bevelSize: safeBevelSize,
      bevelOffset: params.bevelOffset ?? 0,
      bevelSegments: params.bevelSegments ?? 3,
    });
  });

  // Calculate bounding box for each line
  const lineBounds = lineGeometries.map((geo) => {
    geo.computeBoundingBox();
    return geo.boundingBox;
  });

  // Find maximum width for centering/alignment reference
  const maxWidth = Math.max(...lineBounds.map((box) => box.max.x - box.min.x));

  // Create a group to hold all lines
  const groupGeometry = new THREE.BufferGeometry();
  const positions = [];
  const indices = [];
  let indexOffset = 0;

  // Line height (approximate)
  const lineHeight = params.fontSize * 0.01 * 1.2; // 1.2 is line spacing multiplier

  lineGeometries.forEach((geo, i) => {
    const lineWidth = lineBounds[i].max.x - lineBounds[i].min.x;
    let xOffset = 0;

    // Calculate horizontal offset based on alignment
    if (horizontal === 'right') {
      xOffset = -maxWidth / 2; // Align to left edge
    } else if (horizontal === 'left') {
      xOffset = maxWidth / 2 - lineWidth; // Align to right edge
    } else {
      // center
      xOffset = -lineWidth / 2; // Center each line
    }

    // Calculate vertical offset (top to bottom)
    const yOffset = ((lines.length - 1) * lineHeight) / 2 - i * lineHeight;

    // Get position attribute from geometry
    const posAttr = geo.attributes.position;
    const posArray = posAttr.array;

    // Add positions with offset
    for (let j = 0; j < posArray.length; j += 3) {
      positions.push(
        posArray[j] + xOffset,
        posArray[j + 1] + yOffset,
        posArray[j + 2]
      );
    }

    // Add indices with offset
    if (geo.index) {
      const indexArray = geo.index.array;
      for (let j = 0; j < indexArray.length; j++) {
        indices.push(indexArray[j] + indexOffset);
      }
      indexOffset += posAttr.count;
    }

    geo.dispose();
  });

  groupGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3)
  );
  if (indices.length > 0) {
    groupGeometry.setIndex(indices);
  }
  groupGeometry.computeVertexNormals();
  groupGeometry.computeBoundingBox();

  return groupGeometry;
};

function resolveTextMeshId(object, data, type) {
  if (type === 'duplicate') {
    return `text-${Date.now()}`;
  }

  if (!data) {
    return `temp-${Date.now()}`;
  }

  const selected = object?.selectedObject;
  if (selected?.deleteType === 'text' && isLiveSceneObject3D(selected)) {
    return (
      selected.name || selected._id || selected.userData?.text_material?.id
    );
  }

  return (
    data.id ||
    data.userData?.text_material?.id ||
    (isLiveSceneObject3D(data) ? data.name || data._id || data.url : null) ||
    data._id ||
    data.name ||
    `temp-${Date.now()}`
  );
}

export const handleAddText = async (object, data, type = 'add', options = {}) => {
  const { scene, selectedObject } = object;
  const attachToParent = options.attachToParent || null;
  const localTransform = options.localTransform || null;

  const tempId = resolveTextMeshId(object, data, type);

  const state = store.getState();
  const { assets_data } = state.assets_object;

  if (!scene) {
    console.warn('handleAddText: scene is undefined.');
    return;
  }

  if (type === 'duplicate') {
    const sourceId =
      (isLiveSceneObject3D(selectedObject)
        ? selectedObject.name || selectedObject._id || selectedObject.url
        : null) ||
      data?._id ||
      data?.name ||
      data?.url;
    const originalMesh =
      (isLiveSceneObject3D(selectedObject) &&
        selectedObject.deleteType === 'text'
        ? selectedObject
        : null) ||
      (isLiveSceneObject3D(data) ? data : null) ||
      (sourceId ? scene?.getObjectByName(sourceId) : null) ||
      (() => {
        let found = null;
        scene?.traverse((item) => {
          if (found) return;
          if (
            item?.deleteType === 'text' &&
            (item?._id === sourceId ||
              item?.name === sourceId ||
              item?.url === sourceId)
          ) {
            found = item;
          }
        });
        return found;
      })();

    if (!originalMesh) {
      console.warn('Original mesh not found for duplication.');
      return null;
    }

    const clonedMesh = originalMesh.clone();
    clonedMesh.uuid = THREE.MathUtils.generateUUID();

    if (originalMesh.material) {
      clonedMesh.material = originalMesh.material.clone();
    }

    const duplicateBaseName =
      originalMesh.file_name ||
      originalMesh.temp_file_name ||
      originalMesh.name ||
      'Text';

    const duplicateName = getNextCopyName(scene, duplicateBaseName);
    const duplicateId = buildTempFileName(duplicateName);

    clonedMesh._id = duplicateId;
    clonedMesh.url = duplicateId;
    clonedMesh.name = duplicateId;
    clonedMesh.file_name = duplicateName;
    clonedMesh.temp_file_name = duplicateId;
    clonedMesh.asset_file_name = duplicateName;
    clonedMesh.deleteType = 'text';

    clonedMesh.events = data?.events || originalMesh.events || [];
    const { _id: _legacyTextId, ...sourceTextMaterial } =
      originalMesh?.userData?.text_material || {};
    const originalWasBaked =
      !!(originalMesh.userData?.textScaleBaked ||
        originalMesh.userData?.scaleBakedIntoGeometry);
    const originalBakedPanel = originalWasBaked
      ? originalMesh.userData?.bakedPanelScale ||
        originalMesh.userData?.text_material?.bakedTextScale ||
        { x: 1, y: 1, z: 1 }
      : null;

    clonedMesh.userData = {
      ...(originalWasBaked
        ? {
            textScaleBaked: true,
            scaleBakedIntoGeometry: true,
            bakedPanelScale: { ...originalBakedPanel },
          }
        : {}),
      text_material: {
        ...sourceTextMaterial,
        id: duplicateId,
        events: clonedMesh.events,
        ...(originalWasBaked
          ? { bakedTextScale: { ...originalBakedPanel } }
          : {}),
      },
      events: clonedMesh.events,
    };

    clonedMesh.content =
      originalMesh.content ||
      originalMesh.userData?.text_material?.content ||
      '';

    if (attachToParent) {
      attachToParent.add(clonedMesh);
      if (localTransform) {
        clonedMesh.position.copy(localTransform.position);
        clonedMesh.rotation.copy(localTransform.rotation);
        clonedMesh.scale.copy(localTransform.scale);
      } else {
        clonedMesh.position.copy(originalMesh.position);
        clonedMesh.rotation.copy(originalMesh.rotation);
        clonedMesh.scale.copy(originalMesh.scale);
      }
      clonedMesh.updateMatrixWorld(true);
    } else {
      clonedMesh.position.x += 0.5;
      clonedMesh.position.y += 0.5;
      scene.add(clonedMesh);
    }

    store.dispatch(setPushIntersectedObject(clonedMesh));
    store.dispatch(addSceneText(clonedMesh));
    copyPhysicsToDuplicate(originalMesh, clonedMesh);

    syncTextAssetToRedux(clonedMesh);

    if (attachToParent) {
      const parentId =
        attachToParent._id || attachToParent.name || attachToParent.url;
      const list = store.getState().assets_object?.assets_data?.text || [];
      const updated = list.map((entry) => {
        const entryId =
          entry._id || entry.id || entry.name || entry.url || entry.temp_file_name;
        if (entryId !== duplicateId) return entry;
        return { ...entry, parent: parentId };
      });
      store.dispatch(update_assets_data({ type: 'text', data: updated }));
    } else {
      store.dispatch(selected_object(clonedMesh));
      selectObjectInScene(clonedMesh);
      toast.success(`Text duplicated successfully as: "${duplicateName}"`);
    }

    return clonedMesh;
  }

  const params = data || TextInitialData;
  const is3D = params.textType == '3D';
  const loadingPublishedAtUnitScale =
    data?.scale && isPublishedUnitScale(data.scale);
  const bakedScale = loadingPublishedAtUnitScale
    ? resolvePublishedBakedScale(data, params, is3D)
    : null;
  const geometryParams = bakedScale
    ? buildPublishedTextParams(params, bakedScale, is3D)
    : params;

  const fontLoader = new FontLoader();

  const existingMesh = scene?.getObjectByName(tempId);

  // Capture panel-space scale BEFORE text_material.extrusion is updated, so
  // property rebuilds (extrusion etc.) keep the user's X/Y/Z instead of
  // resetting Z to "1" and breaking locked uniform scaling (e.g. 10→5 → 2.5).
  const preservedPanelScale =
    existingMesh &&
    !data?.scale &&
    !isGridHistoryRestoreInProgress()
      ? sceneScaleToPanelScale(existingMesh, existingMesh.scale)
      : null;

  const storedFontPath = existingMesh?.userData?.text_material?.fontPath;
  const shouldRecalculateFontPath =
    !existingMesh ||
    !isUsableFontPath(storedFontPath) ||
    params.fontFamily !== existingMesh?.userData?.text_material?.fontFamily ||
    params.fontWeight !== existingMesh?.userData?.text_material?.fontWeight;

  const resolvedFontPath = shouldRecalculateFontPath
    ? await findFontPath(params.fontFamily, params.fontWeight)
    : storedFontPath;
  const fontPath = isUsableFontPath(resolvedFontPath)
    ? resolvedFontPath.trim()
    : await findFontPath(params.fontFamily, params.fontWeight);

  return new Promise((resolve, reject) => {
    loadFontWithFallback(fontLoader, fontPath)
      .then((font) => {
        try {
          // Use new aligned text geometry function
          const textGeometry = createAlignedTextGeometry(
            geometryParams.content,
            font,
            geometryParams,
            geometryParams.horizontal || 'center'
          );

          const textMaterial = is3D
            ? new THREE.MeshStandardMaterial({
              color: geometryParams.color,
              metalness: geometryParams.metalness ?? 0.3,
              roughness: geometryParams.roughness ?? 0.4,
              side: THREE.FrontSide,
            })
            : new THREE.MeshStandardMaterial({
              color: geometryParams.color,
              side: THREE.DoubleSide,
            });

          let textMesh;

          const rawEvents = existingMesh
            ? (existingMesh.events ??
              existingMesh.userData?.events ??
              existingMesh.userData?.text_material?.events ??
              [])
            : data?.events || data?.userData?.events || [];
          const normalizedEvents = normalizeEventsForScene(
            rawEvents,
            buildPublishedAssetIdLookup(assets_data)
          );

          if (existingMesh) {
            existingMesh.geometry.dispose();
            existingMesh.geometry = textGeometry;
            existingMesh.material.dispose();
            existingMesh.material = textMaterial;
            existingMesh.events = normalizedEvents;
            const { _id: _legacyTextId, ...cleanParams } = params;
            existingMesh.userData = {
              ...existingMesh.userData,
              events: normalizedEvents,
              text_material: {
                ...cleanParams,
                id: tempId,
                metalness: params.metalness ?? 0.3,
                roughness: params.roughness ?? 0.4,
                events: normalizedEvents,
                fontPath: fontPath,
                ...(bakedScale ? { bakedTextScale: bakedScale } : {}),
                ...(existingMesh.userData?.pivotEdited ||
                params?.pivot_edited ||
                data?.pivot_edited
                  ? {
                      pivot_edited: true,
                      pivot_content_matrix:
                        data?.pivot_content_matrix ||
                        params?.pivot_content_matrix ||
                        existingMesh.userData?.pivotContentMatrix ||
                        existingMesh.userData?.text_material
                          ?.pivot_content_matrix,
                    }
                  : {}),
              },
            };
            existingMesh.castShadow = is3D;
            textMesh = existingMesh;
          } else {
            textMesh = new THREE.Mesh(textGeometry, textMaterial);
            textMesh.castShadow = is3D;
            textMesh.deleteType = 'text';
            textMesh._id = tempId;
            textMesh.temp_file_name = data?.temp_file_name || tempId;
            textMesh.name = tempId;
            textMesh.temp_file_name = data?.temp_file_name || tempId;

            if (data?._id) {
              textMesh._id = data._id;
              textMesh.name = data._id;
              textMesh.url = data._id;
            }
            attachEventsToSceneObject(textMesh, normalizedEvents);
            const { _id: _legacyTextId, ...cleanParams } = params;
            textMesh.userData = {
              ...textMesh.userData,
              text_material: {
                ...cleanParams,
                id: tempId,
                metalness: params.metalness ?? 0.3,
                roughness: params.roughness ?? 0.4,
                fontPath: fontPath,
                ...(bakedScale ? { bakedTextScale: bakedScale } : {}),
              },
            };

            const textName = getNextTextName(scene);
            textMesh.file_name = params.file_name || textName;
            textMesh.asset_file_name = params.asset_file_name || textName;
            scene.add(textMesh);
            store.dispatch(setPushIntersectedObject(textMesh));
            store.dispatch(addSceneText(textMesh));
          }
          textMesh.content = params.content;
          textMesh.url = data?._id || data?.id || tempId;
          // Live TextGeometry uses extrusion as scene Z — not a baked GLB.
          setTextScaleBakedIntoGeometry(textMesh, false);

          if (data?.position) {
            textMesh.position.set(
              data.position.x || 0,
              data.position.y || 0,
              data.position.z || 0
            );
          }

          if (data?.rotation) {
            textMesh.rotation.set(
              data.rotation.x !== undefined
                ? THREE.MathUtils.degToRad(data.rotation.x)
                : 0,
              data.rotation.y !== undefined
                ? THREE.MathUtils.degToRad(data.rotation.y)
                : 0,
              data.rotation.z !== undefined
                ? THREE.MathUtils.degToRad(data.rotation.z)
                : 0
            );
          }

          if (data?.scale) {
            const sx = Number(data.scale.x);
            const sy = Number(data.scale.y);
            const sz = Number(data.scale.z);
            if (loadingPublishedAtUnitScale) {
              applyTextEditorScale(textMesh, geometryParams, is3D);
            } else {
              applyPanelScaleToTextObject(
                textMesh,
                Number.isFinite(sx) && sx !== 0 ? sx : 1,
                Number.isFinite(sy) && sy !== 0 ? sy : 1,
                Number.isFinite(sz) && sz !== 0 ? sz : 1
              );
            }
          } else if (!existingMesh) {
            applyTextEditorScale(textMesh, params, is3D);
          } else if (!isGridHistoryRestoreInProgress()) {
            if (preservedPanelScale) {
              // Keep authored panel XYZ through extrusion / property rebuilds.
              applyPanelScaleToTextObject(
                textMesh,
                preservedPanelScale.x,
                preservedPanelScale.y,
                preservedPanelScale.z
              );
            } else {
              textMesh.scale.x = textMesh.scale.x || 1;
              textMesh.scale.y = textMesh.scale.y || 1;
              textMesh.scale.z = getTextEditorScaleZ(params, is3D);
            }
          }

          const box = new THREE.Box3().setFromObject(textMesh);
          const size = box.getSize(new THREE.Vector3());

          // Store alignment for future reference
          textMesh.horizontal = params.horizontal || 'center';
          textMesh.vertical = params.vertical || 'center';
          attachEventsToSceneObject(textMesh, normalizedEvents);

          // Handle vertical alignment of the entire text block
          // Skip when pivot was authored — position_x/y/z is the pivot, not the glyph center.
          const initialProp =
            textMesh.userData.initialProps || textMesh.position;

          const pivotEdited = !!(
            data?.pivot_edited ||
            data?.pivotEdited ||
            existingMesh?.userData?.pivotEdited ||
            existingMesh?.pivotEdited ||
            params?.pivot_edited ||
            (Array.isArray(data?.pivot_content_matrix) &&
              data.pivot_content_matrix.length === 16) ||
            (Array.isArray(existingMesh?.userData?.pivotContentMatrix) &&
              existingMesh.userData.pivotContentMatrix.length === 16) ||
            (Array.isArray(params?.pivot_content_matrix) &&
              params.pivot_content_matrix.length === 16)
          );

          if (
            !pivotEdited &&
            !isGridHistoryRestoreInProgress() &&
            params.vertical !== textMesh.userData.lastVertical
          ) {
            if (params.vertical === 'bottom') {
              textMesh.position.y = initialProp.y - size.y / 2;
            } else if (params.vertical === 'top') {
              textMesh.position.y = initialProp.y + size.y / 2;
            } else {
              textMesh.position.y = initialProp.y;
            }
            textMesh.userData.lastVertical = params.vertical;
          }

          if (
            !textMesh.userData.initialProps &&
            !isGridHistoryRestoreInProgress()
          ) {
            textMesh.userData.initialProps = {
              x: textMesh.position.x,
              y: textMesh.position.y,
            };
          }

          // Re-apply authored pivot offset after geometry rebuild (publish reload
          // recreates TextGeometry and would otherwise snap glyphs onto the pivot).
          const pivotMatrix =
            data?.pivot_content_matrix ||
            params?.pivot_content_matrix ||
            existingMesh?.userData?.pivotContentMatrix ||
            textMesh.userData?.pivotContentMatrix ||
            existingMesh?.userData?.text_material?.pivot_content_matrix;
          const pivotOffset =
            data?.pivot_offset ||
            params?.pivot_offset ||
            existingMesh?.userData?.pivotOffset ||
            textMesh.userData?.pivotOffset ||
            existingMesh?.userData?.text_material?.pivot_offset ||
            params?.pivot_offset;

          if (Array.isArray(pivotMatrix) && pivotMatrix.length === 16) {
            applyStoredPivotContentMatrix(textMesh, pivotMatrix);
          } else if (pivotOffset) {
            applyStoredPivotOffset(textMesh, pivotOffset);
          } else if (pivotEdited) {
            textMesh.userData = textMesh.userData || {};
            textMesh.userData.pivotEdited = true;
            textMesh.pivotEdited = true;
          }

          if (data?.visible === false) {
            textMesh.visible = false;
          }

          const shouldReselect =
            !isGridHistorySuppressed() &&
            data?.visible !== false &&
            (!selectedObject ||
              selectedObject.uuid !== textMesh.uuid ||
              selectedObject.name !== textMesh.name);
          if (shouldReselect) {
            selectObjectInScene(textMesh);
          }
          markTextMeshGeometryBuilt(textMesh);
          syncTextAssetToRedux(textMesh);
          resolve(textMesh);
        } catch (error) {
          reject(error);
        }
      })
      .catch((error) => reject(error));
  });
};

const DEFAULT_FONT_PATH = '/fonts/threejs/helvetiker_regular.typeface.json';

function isUsableFontPath(path) {
  if (!path || typeof path !== 'string') return false;
  const trimmed = path.trim();
  if (!trimmed) return false;
  // Remote/API URLs often return HTML error pages that crash FontLoader.
  if (/^https?:\/\//i.test(trimmed)) return false;
  return trimmed.startsWith('/fonts/threejs/');
}

/**
 * Three r182 FontLoader does not catch JSON.parse — HTML 404/SPA pages throw
 * "Unexpected token '<'". Load via fetch and fall back to the default font.
 */
async function loadFontWithFallback(fontLoader, primaryPath) {
  const paths = [];
  if (isUsableFontPath(primaryPath)) paths.push(primaryPath.trim());
  if (!paths.includes(DEFAULT_FONT_PATH)) paths.push(DEFAULT_FONT_PATH);

  let lastError;
  for (const path of paths) {
    try {
      const res = await fetch(path);
      const text = await res.text();
      if (!res.ok) {
        throw new Error(`Font HTTP ${res.status} at ${path}`);
      }
      if (text.trimStart().startsWith('<')) {
        throw new Error(`Font URL returned HTML instead of JSON: ${path}`);
      }
      return fontLoader.parse(JSON.parse(text));
    } catch (err) {
      lastError = err;
      console.warn(`[text] font load failed (${path}):`, err?.message || err);
    }
  }
  throw lastError || new Error('Unable to load any text font');
}

const findFontPath = async (font, weight) => {
  if (!font || !weight) {
    console.warn('Font family or font weight not provided.');
    return DEFAULT_FONT_PATH;
  }

  const fontLower = font.toLowerCase().trim();
  const weightLower = String(weight).toLowerCase().trim();

  const isBold =
    weightLower.includes('bold') ||
    weightLower === '700' ||
    weightLower === '800' ||
    weightLower === '900' ||
    parseInt(weightLower) >= 700;

  let fontCategory = 'sans';

  if (
    fontLower.includes('ti      mes') ||
    fontLower.includes('serif') ||
    fontLower.includes('georgia') ||
    fontLower.includes('palatino') ||
    fontLower.includes('garamond') ||
    fontLower.includes('gentilis')
  ) {
    fontCategory = 'serif';
  } else if (
    fontLower.includes('courier') ||
    fontLower.includes('mono') ||
    fontLower.includes('consolas') ||
    fontLower.includes('monaco') ||
    fontLower.includes('menlo') ||
    fontLower.includes('optimer')
  ) {
    fontCategory = 'mono';
  } else if (
    fontLower.includes('arial') ||
    fontLower.includes('helvetica') ||
    fontLower.includes('sans') ||
    fontLower.includes('verdana') ||
    fontLower.includes('tahoma') ||
    fontLower.includes('geneva') ||
    fontLower.includes('lucida') ||
    fontLower.includes('trebuchet') ||
    fontLower.includes('droid') ||
    fontLower.includes('helvetiker') ||
    fontLower.includes('abeezee')
  ) {
    fontCategory = 'sans';
  }

  const fontMap = {
    cinzel: '/fonts/threejs/cinzel_regular.typeface.json',
    'indie flower': '/fonts/threejs/indie_flower_regular.typeface.json',
    tangerine: '/fonts/threejs/tangerine_regular.typeface.json',
    'new rocker': '/fonts/threejs/new_rocker_regular.typeface.json',
    'leckerli one': '/fonts/threejs/leckerli_one_regular.typeface.json',
    allura: '/fonts/threejs/allura_regular.typeface.json',
    signature: '/fonts/threejs/allura_regular.typeface.json',
    'dancing script': '/fonts/threejs/dancing_script_regular.typeface.json',
    playwrite: '/fonts/threejs/dancing_script_regular.typeface.json',
    'press start 2p': '/fonts/threejs/press_start_2p_regular.typeface.json',
    doto: '/fonts/threejs/press_start_2p_regular.typeface.json',
    roboto: '/fonts/threejs/roboto_regular.typeface.json',
    montserrat: '/fonts/threejs/montserrat_regular.typeface.json',
    'open sans': '/fonts/threejs/open_sans_regular.typeface.json',
  };

  for (const [key, path] of Object.entries(fontMap)) {
    if (fontLower.includes(key)) {
      return path;
    }
  }

  const fontPaths = {
    sans: {
      regular: '/fonts/threejs/helvetiker_regular.typeface.json',
      bold: '/fonts/threejs/helvetiker_bold.typeface.json',
    },
    serif: {
      regular: '/fonts/threejs/gentilis_regular.typeface.json',
      bold: '/fonts/threejs/gentilis_bold.typeface.json',
    },
    mono: {
      regular: '/fonts/threejs/optimer_regular.typeface.json',
      bold: '/fonts/threejs/optimer_bold.typeface.json',
    },
  };

  const weightKey = isBold ? 'bold' : 'regular';
  const fontPath = fontPaths[fontCategory]?.[weightKey];

  if (fontPath) {
    return fontPath;
  }

  console.warn(
    `Could not resolve font: ${font} with weight: ${weight}. Using default.`
  );
  return DEFAULT_FONT_PATH;
};

export const exportTextMeshToGLB = (mesh) => {
  return new Promise((resolve, reject) => {
    if (!mesh || !(mesh instanceof THREE.Mesh)) {
      reject('Invalid mesh provided.');
      return;
    }

    const exportMesh = mesh.clone();
    exportMesh.geometry = mesh.geometry.clone();

    // Bake local scale into geometry so published transform stays 1,1,1
    const { x, y, z } = mesh.scale;
    const scaleMatrix = new THREE.Matrix4().makeScale(x, y, z);
    exportMesh.geometry.applyMatrix4(scaleMatrix);

    // Placement lives in asset JSON (position_x/y/z). Keep only local content,
    // including any pivot-edit geometry compensation already on the mesh.
    exportMesh.position.set(0, 0, 0);
    exportMesh.rotation.set(0, 0, 0);
    exportMesh.scale.set(1, 1, 1);
    exportMesh.updateMatrix();

    if (mesh.userData?.pivotEdited || mesh.pivotEdited) {
      exportMesh.userData = {
        ...(exportMesh.userData || {}),
        pivotEdited: true,
        pivot_edited: true,
        pivotOffset: mesh.userData?.pivotOffset,
        pivotContentMatrix: mesh.userData?.pivotContentMatrix,
      };
    }

    const exporter = new GLTFExporter();

    exporter.parse(
      exportMesh,
      (result) => {
        exportMesh.geometry.dispose();
        if (result instanceof ArrayBuffer) {
          const blob = new Blob([result], { type: 'model/gltf-binary' });
          resolve(blob);
        } else {
          reject('Unexpected GLTF export format');
        }
      },
      (error) => {
        exportMesh.geometry.dispose();
        reject('GLB export failed: ' + error);
      },
      { binary: true }
    );
  });
};

/**
 * Load published text from its .glb (geometry already includes pivot compensation
 * + baked scale). Falls back to null so caller can rebuild from text_material.
 */
export async function loadPublishedTextMeshFromGlb(object, text) {
  const { scene, selectedObject, GLTFLoader: InjectedLoader } = object || {};
  const url = text?.asset_file_path || text?.asset_file_pathh;
  if (!scene || !url) return null;

  const LoaderCtor = InjectedLoader || GLTFLoader;
  const loader = new LoaderCtor();

  let gltf;
  try {
    gltf = await new Promise((resolve, reject) => {
      loader.load(url, resolve, undefined, reject);
    });
  } catch (err) {
    console.warn('[text] GLB load failed, falling back to TextGeometry:', err);
    return null;
  }

  let sourceMesh = null;
  gltf.scene.traverse((child) => {
    if (!sourceMesh && child.isMesh && child.geometry) sourceMesh = child;
  });
  if (!sourceMesh?.geometry) return null;

  const tm = text?.text_material || {};
  const tempId = text._id || text.id || text.temp_file_name || `text-${Date.now()}`;
  const is3D = (tm.textType || text.textType) === '3D';
  const textMaterial = is3D
    ? new THREE.MeshStandardMaterial({
        color: tm.color || text.color || '#ffffff',
        metalness: tm.metalness ?? text.metalness ?? 0.3,
        roughness: tm.roughness ?? text.roughness ?? 0.4,
        side: THREE.FrontSide,
      })
    : new THREE.MeshStandardMaterial({
        color: tm.color || text.color || '#ffffff',
        side: THREE.DoubleSide,
      });

  const existingMesh = scene.getObjectByName(tempId);
  let textMesh;
  if (existingMesh?.isMesh) {
    existingMesh.geometry?.dispose?.();
    existingMesh.material?.dispose?.();
    existingMesh.geometry = sourceMesh.geometry.clone();
    existingMesh.material = textMaterial;
    textMesh = existingMesh;
  } else {
    textMesh = new THREE.Mesh(sourceMesh.geometry.clone(), textMaterial);
    textMesh.deleteType = 'text';
    textMesh._id = tempId;
    textMesh.name = tempId;
    textMesh.url = tempId;
    textMesh.temp_file_name = text.temp_file_name || tempId;
    scene.add(textMesh);
    store.dispatch(setPushIntersectedObject(textMesh));
    store.dispatch(addSceneText(textMesh));
  }

  textMesh.castShadow = is3D;
  textMesh.deleteType = 'text';
  textMesh._id = tempId;
  textMesh.name = tempId;
  textMesh.url = tempId;
  textMesh.file_name = text.file_name || tm.file_name;
  textMesh.asset_file_name = text.asset_file_name || text.file_name;
  textMesh.temp_file_name = text.temp_file_name || tempId;
  textMesh.asset_file_path = url;
  textMesh.content = tm.content || text.content;
  textMesh.horizontal = tm.horizontal || 'center';
  textMesh.vertical = tm.vertical || 'center';
  textMesh.events = text.events || tm.events || [];

  textMesh.position.set(
    text.position_x ?? 0,
    text.position_y ?? 0,
    text.position_z ?? 0
  );
  textMesh.rotation.set(
    THREE.MathUtils.degToRad(text.rotation_x || 0),
    THREE.MathUtils.degToRad(text.rotation_y || 0),
    THREE.MathUtils.degToRad(text.rotation_z || 0)
  );
  // Scale was baked into the GLB during export.
  textMesh.scale.set(1, 1, 1);

  const pivotEdited = !!(
    text.pivot_edited ||
    tm.pivot_edited ||
    sourceMesh.userData?.pivotEdited ||
    text.pivot_content_matrix ||
    tm.pivot_content_matrix ||
    text.pivot_offset ||
    tm.pivot_offset
  );

  textMesh.userData = {
    ...(textMesh.userData || {}),
    events: textMesh.events,
    // Keep panel scale 1:1 with scene — do not divide Z by extrusion.
    scaleBakedIntoGeometry: true,
    text_material: {
      ...tm,
      id: tempId,
      fontPath:
        (isUsableFontPath(tm.fontPath) && tm.fontPath.trim()) ||
        (await findFontPath(
          tm.fontFamily || text.fontFamily,
          tm.fontWeight || text.fontWeight || 'regular'
        )),
      ...(pivotEdited
        ? {
            pivot_edited: true,
            pivot_content_matrix:
              text.pivot_content_matrix || tm.pivot_content_matrix,
            pivot_offset: text.pivot_offset || tm.pivot_offset,
          }
        : {}),
    },
  };

  if (pivotEdited) {
    textMesh.userData.pivotEdited = true;
    textMesh.pivotEdited = true;
    if (text.pivot_content_matrix || tm.pivot_content_matrix) {
      textMesh.userData.pivotContentMatrix =
        text.pivot_content_matrix || tm.pivot_content_matrix;
    }
    if (text.pivot_offset || tm.pivot_offset) {
      textMesh.userData.pivotOffset = text.pivot_offset || tm.pivot_offset;
    }
  }

  if (text.visible === false) textMesh.visible = false;

  attachEventsToSceneObject(textMesh, textMesh.events);
  markTextMeshGeometryBuilt(textMesh);
  syncTextAssetToRedux(textMesh);

  if (
    !isGridHistorySuppressed() &&
    text.visible !== false &&
    (!selectedObject || selectedObject.name !== textMesh.name)
  ) {
    selectObjectInScene(textMesh);
  }

  return textMesh;
}